import { RuleId } from '@wix/a11y-rules';
import { Box } from '@wix/design-system';
import { useExperiments, useTranslation } from '@wix/fe-essentials/react';
import { GroupExpansionStyle, GroupType, IssueBadge, ToggleType } from '@wix/rules-panel-ui';
import { useSetAtom } from 'jotai';
import { groupBy } from 'lodash';
import React, { useCallback, useRef } from 'react';
import { currentTaskInfoAtom } from '../../atoms/bi-atoms';
import { GroupLoadingSkeleton } from '../../components/wizard-content/group-loading-skeleton';
import { RuleResultsSkeleton } from '../../components/wizard-content/rule-results-skeleton';
import { VerifyPageButton } from '../../components/wizard-content/verify-page-button';
import { usePanelStateAtom } from '../../hooks/rules-state';
import { useBiLogger } from '../../hooks/use-bi-logger';
import { getScanFailureContent } from '../../utils/get-scan-failure-message';
import { resolveGroupAnalysisState } from '../../utils/resolve-group-analysis-state';
import { useInaccessibleComponentsRule } from '../page-level-rules';
import { getNumberOfIssuesInGroup } from '../shared/helpers';
import { useVerify } from '../shared/use-verify';
export const useBuildMembersCustomPageRuleGroups = () => {
  const isAnalyzingRef = useRef({});
  const biLogger = useBiLogger();
  const {
    t
  } = useTranslation();
  const {
    experiments
  } = useExperiments();
  const addErrorModals = experiments.enabled('specs.a11y.addErrorModals');
  const {
    getInaccessibleComponents
  } = useInaccessibleComponentsRule();
  const setCurrentTaskInfo = useSetAtom(currentTaskInfoAtom);
  const {
    getGroupData,
    panelState
  } = usePanelStateAtom();
  const {
    verify
  } = useVerify({
    disableAxe: true
  });
  const buildMembersCustomPageRuleGroup = useCallback(props => {
    const {
      pageId,
      onOpen
    } = props;
    const groupData = getGroupData(pageId);
    if (!groupData) {
      throw new Error(`Group data not found for pageId: ${props.pageId}.`);
    }
    const {
      title,
      ruleExecutionResults,
      rulesStateMap,
      analysisState,
      failureReason
    } = groupData;
    const resultsGroupedByRule = groupBy(ruleExecutionResults, 'ruleId');
    const inaccessibleComponentsRuleResults = resultsGroupedByRule[RuleId.INACCESSIBLE_COMPONENTS];
    const ruleResults = [];
    if (inaccessibleComponentsRuleResults) {
      const inaccessibleComponentsRule = getInaccessibleComponents({
        ...props,
        pageId,
        ruleExecutionResult: inaccessibleComponentsRuleResults
      });
      if (inaccessibleComponentsRule) {
        ruleResults.push(inaccessibleComponentsRule);
      }
    }
    const numberOfIssues = getNumberOfIssuesInGroup(rulesStateMap);
    const handleVerifyClick = async () => {
      var _panelState$globalSec;
      await verify(groupData, [...(((_panelState$globalSec = panelState.globalSectionComponentsDataMap) == null ? void 0 : _panelState$globalSec.values().map(_ref => {
        let {
          componentId
        } = _ref;
        return componentId;
      })) ?? [])]);
    };
    const {
      isFirstAnalysis,
      isAnalyzeFailed,
      isPendingResults,
      isAnalyzing: isAnalyzingState,
      isFetchResultsFailed
    } = resolveGroupAnalysisState(analysisState);
    const isAnalyzing = isAnalyzingState || isFirstAnalysis;
    if (isAnalyzingRef.current[pageId] && !isAnalyzing && groupData.analysisData) {
      var _groupData$analysisDa;
      biLogger.pageScanFinished({
        analysisJobId: (groupData == null || (_groupData$analysisDa = groupData.analysisData) == null ? void 0 : _groupData$analysisDa.analysisJobId) ?? '',
        issuesCount: isAnalyzeFailed ? -1 : numberOfIssues,
        pageId,
        pageType: groupData.itemType
      });
    }
    isAnalyzingRef.current[pageId] = isAnalyzing;
    return {
      disabled: isFirstAnalysis || isAnalyzeFailed,
      expansionMode: GroupExpansionStyle.collapsible,
      groupId: pageId,
      groupType: GroupType.StaticPage,
      isFetchResultsFailed,
      isLoading: isPendingResults || isAnalyzing,
      onGroupToggle: function (toggleType, groupIndex, initiatedByUser) {
        if (initiatedByUser === void 0) {
          initiatedByUser = false;
        }
        setCurrentTaskInfo(taskInfo => ({
          ...taskInfo,
          page_id: pageId,
          page_index: groupIndex,
          page_type: GroupType.StaticPage
        }));
        biLogger.pageExpandCollapse({
          initiatedByUser,
          pageId,
          pageIndex: groupIndex,
          pageType: GroupType.StaticPage,
          ruleResults,
          toggleType
        });
        toggleType === ToggleType.Expand && onOpen();
      },
      ruleGroupContent: {
        badge: isAnalyzeFailed ? /*#__PURE__*/React.createElement(IssueBadge, {
          label: t('accessibility-wizard.rule-group.issue-count-badge.label.scan-failed')
        }) : numberOfIssues !== 0 && !isAnalyzing ? /*#__PURE__*/React.createElement(IssueBadge, {
          label: t('accessibility-wizard.rule-group.issue-count-badge.label', {
            numberOfIssues
          })
        }) : undefined,
        badgeTooltipContent: isAnalyzeFailed ? addErrorModals ? getScanFailureContent(t, failureReason).tooltip : t('accessibility-wizard.rule-group.issue-count-badge.tooltip-content.scan-failed') : undefined,
        collapsedNode: isFirstAnalysis ? /*#__PURE__*/React.createElement(GroupLoadingSkeleton, null) : isAnalyzeFailed || isAnalyzing ? /*#__PURE__*/React.createElement(Box, {
          padding: "0 3px"
        }, /*#__PURE__*/React.createElement(VerifyPageButton, {
          failedTaskCount: numberOfIssues,
          isLoading: isAnalyzing,
          pageId: pageId,
          taskCount: ruleResults.length,
          onClick: handleVerifyClick
        })) : undefined,
        expandNode: /*#__PURE__*/React.createElement(VerifyPageButton, {
          failedTaskCount: numberOfIssues,
          isLoading: isAnalyzing,
          pageId: pageId,
          taskCount: ruleResults.length,
          onClick: handleVerifyClick
        }),
        groupLabel: title
      },
      ruleResults,
      ruleResultsSkeletonComponent: /*#__PURE__*/React.createElement(RuleResultsSkeleton, {
        analysisState: analysisState,
        pageId: pageId
      })
    };
  }, [getInaccessibleComponents, getGroupData, t, verify, panelState.globalSectionComponentsDataMap, setCurrentTaskInfo, biLogger, addErrorModals]);
  return {
    buildMembersCustomPageRuleGroup
  };
};
//# sourceMappingURL=use-build-members-custom-page-rule-group.js.map